import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { adminService } from '../../services/adminService';
import { CommissionSummary, Deal, ServiceRequest, SurveyorMetrics, SurveyorProfile } from '../../types';
import { AnimatedIcon, IconName } from '../../components/icons';
import { toNumber } from '../../utils/number';

interface Stats {
  total_clients: number;
  total_surveyors: number;
  total_requests: number;
  active_negotiations: number;
  confirmed_deals: number;
  total_commission: number;
  pending_reviews: number;
}

const EMPTY_STATS: Stats = {
  total_clients: 0,
  total_surveyors: 0,
  total_requests: 0,
  active_negotiations: 0,
  confirmed_deals: 0,
  total_commission: 0,
  pending_reviews: 0,
};

const EMPTY_COMMISSION_SUMMARY: CommissionSummary = {
  period: 'monthly',
  total_deals: 0,
  total_deal_value: 0,
  total_expected_commission: 0,
  by_surveyor: [],
};

const buildStatsFromLists = (requests: ServiceRequest[], deals: Deal[], summary: CommissionSummary | null): Stats => ({
  total_clients: 0,
  total_surveyors: 0,
  total_requests: requests.length,
  active_negotiations: requests.filter((r) => r.status === 'NEGOTIATING').length || deals.filter((d) => d.status === 'NEGOTIATING').length,
  confirmed_deals: deals.filter((d) => d.status === 'CONFIRMED' || d.status === 'CLOSED').length,
  total_commission: summary?.total_expected_commission ?? deals.reduce((sum, d) => sum + (d.expected_commission || 0), 0),
  pending_reviews: requests.filter((r) => r.status === 'UNDER_REVIEW' || r.status === 'REQUEST_SUBMITTED').length,
});

const buildCommissionSummary = (deals: Deal[]): CommissionSummary => {
  const bySurveyor = new Map<string, CommissionSummary['by_surveyor'][number]>();
  deals.forEach((deal) => {
    const dealValue = deal.deal_value || 0;
    const expectedCommission = deal.expected_commission || 0;
    const surveyorId = deal.surveyor?.id || 'unassigned';
    const surveyorName = deal.surveyor
      ? `${deal.surveyor.first_name} ${deal.surveyor.last_name}`.trim()
      : 'Unassigned';
    const current = bySurveyor.get(surveyorId) ?? {
      surveyor_id: surveyorId,
      surveyor_name: surveyorName,
      deals: 0,
      total_value: 0,
      total_commission: 0,
    };
    current.deals += 1;
    current.total_value += dealValue;
    current.total_commission += expectedCommission;
    bySurveyor.set(surveyorId, current);
  });

  return {
    period: 'monthly',
    total_deals: deals.length,
    total_deal_value: deals.reduce((sum, d) => sum + (d.deal_value || 0), 0),
    total_expected_commission: deals.reduce((sum, d) => sum + (d.expected_commission || 0), 0),
    by_surveyor: Array.from(bySurveyor.values()),
  };
};

const buildSurveyorMetrics = (surveyors: SurveyorProfile[], deals: Deal[]): SurveyorMetrics[] => (
  surveyors.map((surveyor) => {
    const surveyorDeals = deals.filter((deal) => deal.surveyor?.id === surveyor.id);
    return {
      surveyor_id: surveyor.id,
      surveyor_name: `${surveyor.first_name} ${surveyor.last_name}`.trim(),
      jobs_assigned: surveyorDeals.length,
      deals_confirmed: surveyorDeals.filter((deal) => deal.status === 'CONFIRMED').length,
      deals_closed: surveyorDeals.filter((deal) => deal.status === 'CLOSED').length,
      rating_average: toNumber(surveyor.rating_average),
      dispute_rate: 0,
    };
  })
);

const CSS = `
.zs-stat-card {
  position: relative;
  overflow: hidden;
  height: 100%;
  border-radius: var(--sc-radius-md);
  border: 1px solid var(--sc-border);
  background: var(--sc-bg-secondary);
  box-shadow: var(--sc-shadow-sm);
  padding: 12px 14px;
  text-decoration: none;
  display: block;
  transition: all var(--sc-transition-base);
}
.zs-stat-card:hover {
  transform: translateY(-2px);
  box-shadow: var(--sc-shadow-md);
}
.zs-stat-card-circle {
  position: absolute;
  top: -16px;
  right: -16px;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  transition: transform var(--sc-transition-slow);
}
.zs-stat-card:hover .zs-stat-card-circle {
  transform: scale(1.15);
}
.zs-stat-card-iconbox {
  width: 32px;
  height: 32px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform var(--sc-transition-base);
}
.zs-stat-card:hover .zs-stat-card-iconbox {
  transform: scale(1.06) rotate(-3deg);
}
.zs-quick-card {
  transition: all var(--sc-transition-base);
}
.zs-quick-card:hover {
  transform: translateY(-4px);
  box-shadow: var(--sc-shadow-lg);
  border-color: var(--sc-brand-primary) !important;
}
.zs-quick-card:hover .zs-quick-icon {
  transform: scale(1.08);
  background: var(--sc-bg-active) !important;
}
.zs-quick-icon {
  transition: all var(--sc-transition-base);
}
`;

export const AdminOverview: React.FC = () => {
  const [stats, setStats] = useState<Stats>(EMPTY_STATS);
  const [isLoading, setIsLoading] = useState(true);
  const [commSummary, setCommSummary] = useState<CommissionSummary>(EMPTY_COMMISSION_SUMMARY);
  const [surveyorMetrics, setSurveyorMetrics] = useState<SurveyorMetrics[]>([]);
  const [hoveredFunnel, setHoveredFunnel] = useState<number | null>(null);
  const [hoveredLeader, setHoveredLeader] = useState<number | null>(null);

  useEffect(() => {
    let mounted = true;

    const loadOverview = async () => {
      setIsLoading(true);
      const [
        dashboardStats,
        commissionSummary,
        metrics,
        requestsResponse,
        dealsResponse,
        surveyorsResponse,
      ] = await Promise.all([
        adminService.getDashboardStats().catch(() => null),
        adminService.getCommissionSummary({ period: 'monthly' }).catch(() => null),
        adminService.getSurveyorMetrics().catch(() => null),
        adminService.listRequests().catch(() => null),
        adminService.listDeals().catch(() => null),
        adminService.listSurveyors().catch(() => null),
      ]);

      if (!mounted) return;

      const requests = requestsResponse?.results ?? [];
      const deals = dealsResponse?.results ?? [];
      const surveyors = surveyorsResponse?.results ?? [];
      const resolvedSummary = commissionSummary ?? (deals.length > 0 ? buildCommissionSummary(deals) : EMPTY_COMMISSION_SUMMARY);

      setCommSummary(resolvedSummary);
      setStats(dashboardStats ?? buildStatsFromLists(requests, deals, resolvedSummary));
      setSurveyorMetrics(metrics ?? buildSurveyorMetrics(surveyors, deals));
      setIsLoading(false);
    };

    loadOverview().catch(() => {
      if (!mounted) return;
      setStats(EMPTY_STATS);
      setCommSummary(EMPTY_COMMISSION_SUMMARY);
      setSurveyorMetrics([]);
      setIsLoading(false);
    });

    return () => {
      mounted = false;
    };
  }, []);

  /* ─── Computed Analytics ──────────────────────────────── */
  const analytics = useMemo(() => {
    const { total_requests, active_negotiations, confirmed_deals, pending_reviews } = stats;

    // Resolve colors manually for funnel display
    const resolvedFunnel = [
      { label: 'Submitted', count: total_requests, color: '#3B82F6' },
      { label: 'Pending Review', count: pending_reviews, color: '#F59E0B' },
      { label: 'In Negotiation', count: active_negotiations, color: '#EA580C' },
      { label: 'Confirmed', count: confirmed_deals, color: '#10B981' },
    ];
    const maxFunnel = Math.max(...resolvedFunnel.map((f) => f.count), 1);

    // Conversion rate
    const conversionRate = total_requests > 0 ? Math.round((confirmed_deals / total_requests) * 100) : 0;

    // Revenue metrics
    const avgDealValue = confirmed_deals > 0 && commSummary.total_deals > 0
      ? commSummary.total_deal_value / commSummary.total_deals
      : 0;
    const commissionRate = commSummary.total_deal_value > 0
      ? ((commSummary.total_expected_commission / commSummary.total_deal_value) * 100).toFixed(1)
      : '0';

    // Surveyor leaderboard (top 5)
    const leaderboard = [...(commSummary?.by_surveyor || [])]
      .sort((a, b) => b.total_value - a.total_value)
      .slice(0, 5);

    // Surveyor performance from metrics
    const topPerformers = [...surveyorMetrics]
      .sort((a, b) => toNumber(b.rating_average) - toNumber(a.rating_average))
      .slice(0, 5);

    const avgRating = surveyorMetrics.length > 0
      ? (surveyorMetrics.reduce((s, m) => s + toNumber(m.rating_average), 0) / surveyorMetrics.length).toFixed(1)
      : '0';
    const avgDisputeRate = surveyorMetrics.length > 0
      ? (surveyorMetrics.reduce((s, m) => s + toNumber(m.dispute_rate), 0) / surveyorMetrics.length * 100).toFixed(1)
      : '0';

    return { funnel: resolvedFunnel, maxFunnel, conversionRate, avgDealValue, commissionRate, leaderboard, topPerformers, avgRating, avgDisputeRate };
  }, [stats, commSummary, surveyorMetrics]);

  const kpis: { label: string; value: string | number; icon: IconName; color: string; link: string }[] = [
    { label: 'Pending', value: stats.pending_reviews, icon: 'clipboard', color: '#F59E0B', link: '/admin/requests' },
    { label: 'Clients', value: stats.total_clients, icon: 'user', color: '#14B8A6', link: '/admin/clients' },
    { label: 'Surveyors', value: stats.total_surveyors, icon: 'worker', color: '#7C3AED', link: '/admin/surveyors' },
    { label: 'Active Neg', value: stats.active_negotiations, icon: 'chat', color: '#8B5CF6', link: '/admin/negotiations' },
    { label: 'Confirmed', value: stats.confirmed_deals, icon: 'success', color: '#10B981', link: '/admin/deals' },
    { label: 'Comm.', value: `$${toNumber(stats.total_commission).toFixed(2)}`, icon: 'money', color: '#0C5ADB', link: '/admin/deals' },
    { label: 'Total Req', value: stats.total_requests, icon: 'folder', color: '#3B82F6', link: '/admin/requests' },
    { label: 'Conv. %', value: `${analytics.conversionRate}%`, icon: 'chart', color: '#EA580C', link: '/admin/deals' },
  ];

  return (
    <div>
      <style>{CSS}</style>
      <h1 style={s.title}>Admin Overview</h1>
      <p style={s.subtitle}>Live platform statistics, analytics, and quick access.</p>


      {isLoading ? (
        <div style={s.center}><div style={s.spinner} /></div>
      ) : (
        <>
          {/* KPI Cards */}
          <div style={s.kpiGrid}>
            {kpis.map((k) => (
              <Link key={k.label} to={k.link} className="zs-stat-card">
                {/* Top color bar */}
                <div style={{
                  position: 'absolute', top: 0, left: 0, right: 0, height: 3,
                  background: `linear-gradient(90deg, ${k.color}, ${k.color}88)`
                }} />
                {/* Decorative circle */}
                <div className="zs-stat-card-circle" style={{ backgroundColor: `${k.color}0A` }} />
                
                {/* Content inner */}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', position: 'relative', zIndex: 1, gap: 4, textAlign: 'center' }}>
                  <div className="zs-stat-card-iconbox" style={{ background: `linear-gradient(135deg, ${k.color}15 0%, ${k.color}08 100%)`, color: k.color, border: `1px solid ${k.color}12`, margin: '0 auto' }}>
                    <AnimatedIcon name={k.icon} size={16} color={k.color} />
                  </div>
                  <div style={{ fontSize: 9, fontWeight: 700, color: 'var(--sc-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.08em', marginTop: 1 }}>
                    {k.label}
                  </div>
                  <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--sc-text-primary)', letterSpacing: '-0.02em', lineHeight: 1 }}>
                    {k.value}
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {/* ─── Analytics Section ──────────────────────────────── */}
          <div style={s.analyticsGrid}>
              {/* Conversion Funnel */}
              <div style={s.analyticCard}>
                <h3 style={s.analyticTitle}><AnimatedIcon name="chart" size={18} color="var(--sc-text-secondary)" /> Request Conversion Funnel</h3>
                <div style={s.funnelList}>
                  {analytics.funnel.map((f, fi) => {
                    const widthPct = (f.count / analytics.maxFunnel) * 100;
                    const isHov = hoveredFunnel === fi;
                    return (
                      <div key={f.label} style={s.funnelRow}
                        onMouseEnter={() => setHoveredFunnel(fi)}
                        onMouseLeave={() => setHoveredFunnel(null)}>
                        <div style={{ ...s.funnelLabel, color: isHov ? f.color : 'var(--sc-text-primary)', fontWeight: isHov ? 700 : 600, transition: 'color 0.15s' }}>{f.label}</div>
                        <div style={s.funnelBarTrack}>
                          <div style={{
                            borderColor: 'transparent',
                            width: `${widthPct}%`,
                            background: `linear-gradient(90deg, ${f.color}99 0%, ${f.color} 100%)`,
                            boxShadow: isHov ? `inset 0 1px 0 rgba(255,255,255,0.35), 0 0 16px ${f.color}66` : 'none',
                            transform: isHov ? 'scaleY(1.12)' : 'scaleY(1)',
                            transition: 'width 0.6s cubic-bezier(.34,1.56,.64,1), box-shadow 0.2s, transform 0.15s',
                            height: '100%', borderRadius: 13, minWidth: 6, transformOrigin: 'left center'
                          }} />
                        </div>
                        <div style={{ ...s.funnelCount, color: isHov ? f.color : 'var(--sc-text-primary)', transition: 'color 0.15s' }}>{f.count}</div>
                      </div>
                    );
                  })}
                </div>
                <div style={s.funnelFooter}>
                  <div style={s.funnelFootStat}>
                    <div style={s.metricCircle}>
                      <svg width="72" height="72" viewBox="0 0 72 72">
                        <circle cx="36" cy="36" r="30" fill="none" stroke="var(--sc-border)" strokeWidth="6" />
                        <circle cx="36" cy="36" r="30" fill="none" stroke="var(--sc-success)" strokeWidth="6"
                          strokeDasharray={`${(analytics.conversionRate / 100) * 188.5} 188.5`}
                          strokeLinecap="round" transform="rotate(-90 36 36)" />
                      </svg>
                      <span style={s.metricCircleText}>{analytics.conversionRate}%</span>
                    </div>
                    <div style={s.footLabel}>Conversion Rate</div>
                  </div>
                </div>
              </div>

              {/* Revenue & Commission */}
              <div style={s.analyticCard}>
                <h3 style={s.analyticTitle}><AnimatedIcon name="money" size={18} color="var(--sc-text-secondary)" /> Revenue & Commission</h3>
                <div style={s.revenueGrid}>
                  <div style={s.revCard}>
                    <div style={s.revIcon}><AnimatedIcon name="chart" size={22} color="var(--sc-success)" /></div>
                    <div style={{ ...s.revNum, color: 'var(--sc-success)' }}>${commSummary ? commSummary.total_deal_value.toFixed(0) : '0'}</div>
                    <div style={s.revLabel}>Total Deal Value</div>
                  </div>
                  <div style={s.revCard}>
                    <div style={s.revIcon}><AnimatedIcon name="money" size={22} color="var(--sc-brand-primary)" /></div>
                    <div style={{ ...s.revNum, color: 'var(--sc-brand-primary)' }}>${commSummary ? commSummary.total_expected_commission.toFixed(0) : '0'}</div>
                    <div style={s.revLabel}>Total Commission</div>
                  </div>
                  <div style={s.revCard}>
                    <div style={s.revIcon}><AnimatedIcon name="chart" size={22} color="var(--sc-info-text)" /></div>
                    <div style={{ ...s.revNum, color: 'var(--sc-info-text)' }}>{analytics.commissionRate}%</div>
                    <div style={s.revLabel}>Commission Rate</div>
                  </div>
                  <div style={s.revCard}>
                    <div style={s.revIcon}><AnimatedIcon name="star" size={22} color="var(--sc-brand-primary-light)" /></div>
                    <div style={{ ...s.revNum, color: 'var(--sc-brand-primary-light)' }}>${analytics.avgDealValue.toFixed(0)}</div>
                    <div style={s.revLabel}>Avg Deal Value</div>
                  </div>
                </div>
                {/* Commission split bar */}
                {commSummary && commSummary.total_deal_value > 0 && (
                  <div style={{ marginTop: 16 }}>
                    <div style={s.splitLabel}>Revenue Split</div>
                    <div style={s.splitBar}>
                      <div style={{ ...s.splitFill, width: `${100 - parseFloat(analytics.commissionRate)}%`, background: 'var(--sc-success)' }}>
                        <span style={s.splitText}>Surveyor</span>
                      </div>
                      <div style={{ ...s.splitFill, width: `${parseFloat(analytics.commissionRate)}%`, background: 'var(--sc-brand-primary)' }}>
                        <span style={s.splitText}>Platform</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Surveyor Leaderboard */}
              <div style={s.analyticCard}>
                <h3 style={s.analyticTitle}><AnimatedIcon name="star" size={18} color="var(--sc-warning)" /> Top Surveyors by Revenue</h3>
                {analytics.leaderboard.length > 0 ? (
                  <div style={s.leaderboard}>
                    {analytics.leaderboard.map((sv, i) => {
                      const maxVal = analytics.leaderboard[0]?.total_value || 1;
                      const medalColors = ['#FFD700', '#C0C0C0', '#CD7F32', 'var(--sc-text-secondary)', 'var(--sc-text-secondary)'];
                      const widthPct = (sv.total_value / maxVal) * 100;
                      const isHov = hoveredLeader === i;
                      const LEADER_COLORS = ['#6366F1','#06B6D4','#10B981','#F59E0B','#F43F5E'];
                      const lColor = LEADER_COLORS[i % LEADER_COLORS.length];
                      return (
                        <div key={sv.surveyor_id} style={s.leaderRow}
                          onMouseEnter={() => setHoveredLeader(i)}
                          onMouseLeave={() => setHoveredLeader(null)}>
                          <div style={{ ...s.leaderRank, color: medalColors[i] }}><AnimatedIcon name="star" size={20} color={medalColors[i]} /></div>
                          <div style={s.leaderInfo}>
                            <div style={{ ...s.leaderName, color: isHov ? lColor : 'var(--sc-text-primary)', transition: 'color 0.15s' }}>{sv.surveyor_name}</div>
                            <div style={s.leaderMeta}>{sv.deals} deals</div>
                          </div>
                          <div style={s.leaderBarTrack}>
                            <div style={{
                              height: '100%', borderRadius: 9, transformOrigin: 'left center',
                              width: `${widthPct}%`,
                              background: `linear-gradient(90deg, ${lColor}88 0%, ${lColor} 100%)`,
                              boxShadow: isHov ? `0 0 18px ${lColor}66` : 'none',
                              transform: isHov ? 'scaleY(1.18)' : 'scaleY(1)',
                              transition: 'width 0.6s cubic-bezier(.34,1.56,.64,1), box-shadow 0.2s, transform 0.15s',
                            }} />
                          </div>
                          <div style={{ ...s.leaderValue, color: lColor, transition: 'color 0.15s' }}>${(sv.total_value ?? 0).toFixed(0)}</div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p style={{ color: 'var(--sc-text-secondary)', fontSize: 13 }}>No revenue data available yet</p>
                )}
              </div>

              {/* Platform Health */}
              <div style={s.analyticCard}>
                <h3 style={s.analyticTitle}><AnimatedIcon name="shield" size={18} color="var(--sc-text-secondary)" /> Platform Health</h3>
                <div style={s.healthGrid}>
                  <div style={s.healthItem}>
                    <div style={{ ...s.healthNum, color: 'var(--sc-success)' }}>{analytics.avgRating}</div>
                    <div style={s.healthLabel}>Avg Surveyor Rating</div>
                    <div style={s.starsRow}>
                      {[1, 2, 3, 4, 5].map((star) => (
                        <span key={star} style={{ color: star <= Math.round(parseFloat(analytics.avgRating)) ? 'var(--sc-warning)' : 'var(--sc-border)', fontSize: 16 }}>★</span>
                      ))}
                    </div>
                  </div>
                  <div style={s.healthItem}>
                    <div style={{ ...s.healthNum, color: parseFloat(analytics.avgDisputeRate) > 5 ? 'var(--sc-error)' : 'var(--sc-success)' }}>{analytics.avgDisputeRate}%</div>
                    <div style={s.healthLabel}>Dispute Rate</div>
                    <div style={s.healthIndicator}>
                      <div style={{ ...s.healthBar, background: parseFloat(analytics.avgDisputeRate) > 5 ? 'var(--sc-error-bg)' : 'var(--sc-success-bg)' }}>
                        <div style={{ ...s.healthBarFill, width: `${Math.min(parseFloat(analytics.avgDisputeRate) * 10, 100)}%`, background: parseFloat(analytics.avgDisputeRate) > 5 ? 'var(--sc-error-text)' : 'var(--sc-success-text)' }} />
                      </div>
                    </div>
                  </div>
                  <div style={s.healthItem}>
                    <div style={{ ...s.healthNum, color: 'var(--sc-brand-primary-light)' }}>{surveyorMetrics.length}</div>
                    <div style={s.healthLabel}>Active Surveyors</div>
                  </div>
                  <div style={s.healthItem}>
                    <div style={{ ...s.healthNum, color: 'var(--sc-info-text)' }}>{commSummary?.total_deals || 0}</div>
                    <div style={s.healthLabel}>Total Deals</div>
                  </div>
                </div>
              </div>

              {/* Surveyor Performance Table */}
              {analytics.topPerformers.length > 0 && (
                <div style={{ ...s.analyticCard, gridColumn: '1 / -1' }}>
                  <h3 style={s.analyticTitle}><AnimatedIcon name="chart" size={18} color="var(--sc-text-secondary)" /> Surveyor Performance Breakdown</h3>
                  <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch', borderRadius: 10, border: '1px solid var(--sc-border)' }}>
                    <div style={s.perfTable}>
                      <div style={s.perfHead}>
                        <div style={{ ...s.perfTh, flex: 2 }}>Surveyor</div>
                        <div style={s.perfTh}>Asgd</div>
                        <div style={s.perfTh}>Conf</div>
                        <div style={s.perfTh}>Clsd</div>
                        <div style={s.perfTh}>Rating</div>
                        <div style={s.perfTh}>Disp. %</div>
                        <div style={{ ...s.perfTh, flex: 1.5 }}>Perf.</div>
                      </div>
                      {analytics.topPerformers.map((m) => {
                        const perfPct = m.jobs_assigned > 0 ? Math.round((m.deals_confirmed / m.jobs_assigned) * 100) : 0;
                        return (
                          <div key={m.surveyor_id} style={s.perfRow}>
                            <div style={{ ...s.perfTd, flex: 2, fontWeight: 600 }}>{m.surveyor_name}</div>
                            <div style={s.perfTd}>{m.jobs_assigned}</div>
                            <div style={{ ...s.perfTd, color: 'var(--sc-success)', fontWeight: 600 }}>{m.deals_confirmed}</div>
                            <div style={s.perfTd}>{m.deals_closed}</div>
                            <div style={s.perfTd}>
                              <span style={{ color: 'var(--sc-warning)' }}>★</span> {toNumber(m.rating_average).toFixed(1)}
                            </div>
                            <div style={{ ...s.perfTd, color: toNumber(m.dispute_rate) > 0.05 ? 'var(--sc-error)' : 'var(--sc-success-text)' }}>
                              {(toNumber(m.dispute_rate) * 100).toFixed(1)}%
                            </div>
                            <div style={{ ...s.perfTd, flex: 1.5, display: 'flex', alignItems: 'center', gap: 8 }}>
                              <div style={s.perfBarTrack}>
                                <div style={{ ...s.perfBarFill, width: `${perfPct}%`, background: perfPct >= 70 ? 'var(--sc-success)' : perfPct >= 40 ? 'var(--sc-warning)' : 'var(--sc-error)' }} />
                              </div>
                              <span style={{ fontSize: 11, fontWeight: 700 }}>{perfPct}%</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
          </div>
        </>
      )}

      {/* Quick links */}
      <h2 style={{ fontSize: 18, fontWeight: 700, color: 'var(--sc-text-primary)', margin: '32px 0 16px' }}>Quick Actions</h2>
      <div style={s.quickRow}>
        {[
          { label: 'Review Requests', icon: 'clipboard' as IconName, to: '/admin/requests', desc: 'Approve or reject new survey requests' },
          { label: 'Assign Surveyors', icon: 'worker' as IconName, to: '/admin/requests', desc: 'Assign verified surveyors to requests' },
          { label: 'Audit Logs', icon: 'search' as IconName, to: '/admin/audit-logs', desc: 'Track all platform actions' },
          { label: 'Settings', icon: 'settings' as IconName, to: '/admin/settings', desc: 'Commission rates and toggles' },
        ].map((a) => (
          <Link key={a.label} to={a.to} style={s.quickCard} className="zs-quick-card">
            <div style={s.quickIcon} className="zs-quick-icon"><AnimatedIcon name={a.icon} size={24} color="var(--sc-brand-primary)" /></div>
            <div style={{ fontWeight: 700, color: 'var(--sc-text-primary)', marginBottom: 4, fontFamily: "'Outfit', sans-serif" }}>{a.label}</div>
            <div style={{ fontSize: 12, color: 'var(--sc-text-secondary)', fontFamily: "'Inter', sans-serif" }}>{a.desc}</div>
          </Link>
        ))}
      </div>
    </div>
  );
};

const s: Record<string, React.CSSProperties> = {
  title: { fontSize: 26, fontWeight: 800, color: 'var(--sc-text-primary)', margin: '0 0 4px', fontFamily: "'Outfit', sans-serif", letterSpacing: '-0.5px' },
  subtitle: { fontSize: 14, color: 'var(--sc-text-secondary)', margin: '0 0 24px', fontFamily: "'Inter', sans-serif" },
  center: { display: 'flex', justifyContent: 'center', padding: 60 },
  spinner: { width: 32, height: 32, border: '3px solid var(--sc-border)', borderTopColor: 'var(--sc-brand-primary)', borderRadius: '50%', animation: 'spin 0.8s linear infinite' },

  kpiGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))', gap: 10, marginBottom: 24 },
  card: { display: 'block', background: 'var(--sc-bg-secondary)', borderRadius: 16, padding: '16px 18px', border: '1px solid var(--sc-border)', textDecoration: 'none', textAlign: 'center' as const, boxShadow: 'var(--sc-shadow-card)' },
  iconBox: { width: 44, height: 44, borderRadius: '50%', background: 'var(--sc-bg-primary)', border: '1px solid var(--sc-border)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' },
  kpiVal: { fontSize: 24, fontWeight: 800, color: 'var(--sc-text-primary)', margin: '6px 0 2px' },
  kpiLabel: { fontSize: 11, color: 'var(--sc-text-secondary)', fontWeight: 600, textTransform: 'uppercase' as const },

  /* ─── Analytics ─────────────────────────────────────── */
  analyticsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 16, marginBottom: 8 },
  analyticCard: { background: 'var(--sc-bg-secondary)', borderRadius: 16, padding: 22, border: '1px solid var(--sc-border)', boxShadow: 'var(--sc-shadow-card)' },
  analyticTitle: { fontSize: 14, fontWeight: 700, color: 'var(--sc-text-primary)', margin: '0 0 16px', fontFamily: "'Outfit', sans-serif" },

  funnelList: { display: 'flex', flexDirection: 'column' as const, gap: 14, marginBottom: 18 },
  funnelRow: { display: 'flex', alignItems: 'center', gap: 10, cursor: 'default' },
  funnelLabel: { width: 120, fontSize: 12, color: 'var(--sc-text-secondary)' },
  funnelBarTrack: { flex: 1, height: 26, background: 'linear-gradient(90deg, var(--sc-bg-primary) 0%, var(--sc-bg-hover) 100%)', borderRadius: 13, overflow: 'hidden' },
  funnelBarFill: { height: '100%', borderRadius: 13, minWidth: 6, transformOrigin: 'left center' },
  funnelCount: { width: 40, textAlign: 'right' as const, fontSize: 14, fontWeight: 700, color: 'var(--sc-text-primary)' },
  funnelFooter: { display: 'flex', justifyContent: 'center', paddingTop: 14, borderTop: '1px solid var(--sc-border)' },
  funnelFootStat: { display: 'flex', flexDirection: 'column' as const, alignItems: 'center', gap: 6 },
  footLabel: { fontSize: 11, fontWeight: 500, color: 'var(--sc-text-secondary)' },

  metricCircle: { position: 'relative' as const, width: 72, height: 72, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  metricCircleText: { position: 'absolute' as const, fontSize: 16, fontWeight: 800, color: 'var(--sc-success)' },

  revenueGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 },
  revCard: { background: 'var(--sc-bg-secondary)', borderRadius: 12, padding: 16, textAlign: 'center' as const, border: '1px solid var(--sc-border)', boxShadow: 'var(--sc-shadow-card)' },
  revIcon: { width: 44, height: 44, borderRadius: '50%', background: 'var(--sc-bg-primary)', border: '1px solid var(--sc-border)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 10px', flexShrink: 0 },
  revNum: { fontSize: 22, fontWeight: 800, color: 'var(--sc-text-primary)' },
  revLabel: { fontSize: 11, color: 'var(--sc-text-secondary)', fontWeight: 500, marginTop: 2 },
  splitLabel: { fontSize: 11, fontWeight: 600, color: 'var(--sc-text-secondary)', marginBottom: 6 },
  splitBar: { display: 'flex', height: 24, borderRadius: 8, overflow: 'hidden' },
  splitFill: { display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'width 0.4s ease' },
  splitText: { fontSize: 10, fontWeight: 700, color: '#FFF' },

  leaderboard: { display: 'flex', flexDirection: 'column' as const, gap: 12 },
  leaderRow: { display: 'flex', alignItems: 'center', gap: 10, cursor: 'default' },
  leaderRank: { fontSize: 16, width: 28, textAlign: 'center' as const },
  leaderInfo: { width: 120 },
  leaderName: { fontSize: 12, fontWeight: 600, color: 'var(--sc-text-primary)' },
  leaderMeta: { fontSize: 10, color: 'var(--sc-text-secondary)' },
  leaderBarTrack: { flex: 1, height: 18, background: 'linear-gradient(90deg, var(--sc-bg-primary) 0%, var(--sc-bg-hover) 100%)', borderRadius: 9, overflow: 'hidden' },
  leaderBarFill: { height: '100%', borderRadius: 9, transformOrigin: 'left center' },
  leaderValue: { width: 60, textAlign: 'right' as const, fontSize: 13, fontWeight: 700 },

  healthGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 },
  healthItem: { background: 'var(--sc-bg-secondary)', borderRadius: 12, padding: 16, textAlign: 'center' as const, border: '1px solid var(--sc-border)', boxShadow: 'var(--sc-shadow-card)' },
  healthNum: { fontSize: 28, fontWeight: 800, color: 'var(--sc-text-primary)' },
  healthLabel: { fontSize: 11, color: 'var(--sc-text-secondary)', fontWeight: 500, marginTop: 2 },
  starsRow: { marginTop: 4 },
  healthIndicator: { marginTop: 6 },
  healthBar: { height: 6, borderRadius: 3, overflow: 'hidden' },
  healthBarFill: { height: '100%', borderRadius: 3, transition: 'width 0.4s ease' },

  perfTable: { overflow: 'hidden', minWidth: 650 },
  perfHead: { display: 'flex', padding: '10px 16px', background: 'var(--sc-bg-primary)', borderBottom: '1px solid var(--sc-border)' },
  perfTh: { flex: 1, fontSize: 10, fontWeight: 700, color: 'var(--sc-text-secondary)', textTransform: 'uppercase' as const, letterSpacing: 0.5 },
  perfRow: { display: 'flex', padding: '10px 16px', borderBottom: '1px solid var(--sc-divider)', alignItems: 'center' },
  perfTd: { flex: 1, fontSize: 12, color: 'var(--sc-text-primary)' },
  perfBarTrack: { flex: 1, height: 6, background: 'var(--sc-bg-primary)', borderRadius: 3, overflow: 'hidden' },
  perfBarFill: { height: '100%', borderRadius: 3, transition: 'width 0.4s ease' },

  quickRow: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 },
  quickCard: { background: 'var(--sc-bg-secondary)', borderRadius: 16, padding: 20, border: '1px solid var(--sc-border)', textDecoration: 'none', display: 'block', boxShadow: 'var(--sc-shadow-card)', transition: 'all var(--sc-transition-base)' },
  quickIcon: { width: 52, height: 52, borderRadius: '50%', background: 'var(--sc-bg-primary)', border: '1px solid var(--sc-border)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 14, flexShrink: 0 },
};
